import { diaryByIdQueryOptions } from "@/features/diaries/api";
import { resolveTrackerDueEntry } from "@/features/diaries/utils/findEarliestDueEntry";
import { extractTrackerById } from "@/features/diaries/services/extractTrackerById";
import { isTrackerBlockedBySequence } from "@/features/diaries/utils/sequentialFormHelpers";
import { redirectIfConsentRequired } from "@/features/consent/utils/redirectIfConsentRequired";
import PatientsTrackerFormPage from "@/features/pages/components/PatientsTrackerFormPage";
import { formatTrackerLateralityBanner } from "@/features/pages/utils/trackerLateralityBanner";
import { useAppLocale } from "@/features/i18n/LocaleProvider";
import type { FormViewT } from "@patient-watch/types";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { z } from "zod";
import { formBreadcrumbLabel } from "@/features/navigation/services/entityDisplayLabel";
import { useNav } from "@/utils/nav";

const searchSchema = z.object({
    is_unscheduled: z.boolean().optional(),
});

const Page = () => {
    const { tracker } = Route.useLoaderData();
    const { pageIndex, diaryId, trackerId, entryId } = Route.useParams();
    const { is_unscheduled } = Route.useSearch({ strict: false }) ?? {};

    const { locale } = useAppLocale();

    const lateralityBanner = formatTrackerLateralityBanner(tracker?.side, locale);

    useNav([
        { label: "Patients", route: { to: "/patients" } },
        {
            route: { to: "/patients/$diaryId", params: { diaryId } },
            label: "Diary",
        },
        {
            route: {
                to: "/diaries/$diaryId/tracker/$trackerId/entry/$entryId/page/$pageIndex",
                params: { diaryId, trackerId, entryId, pageIndex },
            },
            label: formBreadcrumbLabel(tracker?.form, locale, "Tracker Entry"),
        },
    ]);

    return (
        <div className="px-4 py-4">
            <PatientsTrackerFormPage
                pageIndex={Number(pageIndex)}
                form={tracker?.form as FormViewT}
                route={Route}
                nextEntry={is_unscheduled ? null : tracker ? resolveTrackerDueEntry(tracker) : null}
                diaryId={diaryId}
                lateralityBanner={lateralityBanner}
            />
        </div>
    );
};

export const Route = createFileRoute("/_app/diaries/$diaryId/tracker/$trackerId/entry/$entryId/page/$pageIndex/")({
    validateSearch: searchSchema,
    component: Page,
    loader: async ({ context, params, search }) => {
        const diary = await context?.queryClient.ensureQueryData(diaryByIdQueryOptions(params.diaryId));
        if (!diary) {
            throw new Error("Diary not found");
        }
        const tracker = extractTrackerById(diary, params.trackerId);
        const isPatientViewer = context.auth?.id === diary.patient_id || context.auth?.roles?.isPatientOnly === true;

        redirectIfConsentRequired(diary, context.auth?.id, isPatientViewer);

        if (isTrackerBlockedBySequence(tracker, search?.is_unscheduled)) {
            throw redirect({
                to: "/diaries/$diaryId",
                params: { diaryId: params.diaryId },
            });
        }

        return {
            patientId: diary.patient_id ?? "",
            tracker: tracker,
        };
    },
});
